/* eslint no-param-reassign: 0 , class-methods-use-this: 0 */
/* eslint arrow-body-style: 0, consistent-return: 0 */
import { DirectoryGroupingConsts, ItemTypeConsts } from 'client/util/Constants';
import {
  find,
  flatten,
  map,
  reduce,
} from 'lodash';
import { getFilters } from 'client/api/v2/apiUtils';
import { parallelLimit } from 'async';
import CollectionApi from 'client/api/v2/CollectionApi';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import SystemApi from 'client/api/v2/SystemApi';

class PolicySystemStatusApi extends CollectionApi {
  constructor(policyId = '') {
    super(`/api/v2/policies/${policyId}/policystatuses`, {
      skip: 0,
      limit: 50,
      filter: [
        {
          name: 'state',
          operator: 'ne',
          value: 'UNINSTALL_SUCCESS',
        },
      ],
    });
    this.policyId = policyId;
  }

  setPolicyId(policyId) {
    this.baseUrl = `/api/v2/policies/${policyId || ''}/policystatuses`;
    this.policyId = policyId;
  }

  fetch(skip, limit, sort, searchTerm, cancelToken, fltrs = []) {
    const filters = getFilters(fltrs, this.defaultParams.filter);
    return new Promise((resolve, reject) => {
      const tasks = [];
      const invalidResults = [];
      this.getBoundSystems(this.policyId).then(boundSystems => {
        super
          .fetch(skip, limit, sort, searchTerm, cancelToken, filters)
          .then((response) => {
            const filteredResults = [];
            response.results.forEach((result) => {
              const exists = boundSystems
                .some((boundSystem) => boundSystem.id === result.systemID);
              if (exists || !result.stdErr.includes('Duplicate')) {
                tasks.push((cb) => {
                  SystemApi
                    .get(result.systemID, cancelToken)
                    .then((system) => {
                      // policy statuses are now kept even if a system is deleted so we
                      // have to make sure the system exists before adding the status
                      // to the item table
                      if (system) {
                        result.system = system;
                        filteredResults.push(result);
                      }
                      cb();
                    })
                    .catch(() => {
                      // If unable to look up system for policy results, just
                      // continue. The system may have been deleted while its
                      // old policy results are still available.
                      cb();
                    });
                });
              } else {
                invalidResults.push(result.id);
              }
            }, []);
            parallelLimit(tasks, 5, (error) => {
              if (error) {
                reject(error);
              } else {
                const filteredTotalCount = response.totalCount - invalidResults.length;
                resolve({ results: filteredResults, totalCount: filteredTotalCount });
              }
            });
          })
          .catch((err) => {
          // TODO: Remove these lines when the policystatuses endpoint supports
          // the search operator for system_id and exit_status
            if (searchTerm) {
              resolve({ results: [], totalCount: 0 });
            } else {
              reject(err);
            }
          });
      });
    });
  }

  getAttachedPolicies(systemID) {
    const url = `/api/v2/systems/${systemID}/policies`;
    return this.getAllItems(url, { skip: 0, limit: 100 })
      .then((policies) => {
        if (!policies) return [];
        return policies;
      });
  }

  getBoundSystems(policyID = this.policyId) {
    const url = `/api/v2/policies/${policyID}/systems`;
    return this.getAllItems(url, { skip: 0, limit: 100 })
      .then((boundSystems) => {
        if (!boundSystems) return [];
        return boundSystems;
      });
  }

  getDetails(systemID, policyID) {
    const url = `/api/v2/systems/${systemID}/policies`;
    return this.getAllItems(url, { skip: 0, limit: 100 })
      .then((policies) => {
        if (!policies) return; // return empty array? error? nothing? up to you.

        // get the paths from this policy to the result system
        const policy = find(policies, d => d.id === policyID);
        if (policy) {
          const paths = map(flatten(policy.paths), 'to');
          const groupIDs = reduce(paths, (memo, path) => {
            if (path.type === ItemTypeConsts.systemGroup) memo.push(path.id);
            return memo;
          }, []);
          return this.getDirectoryGroupingApi(groupIDs);
        }
        return [];
      })
      .catch(() => {
        throw new Error('Unable to fetch associated groups!');
      });
  }

  getDirectoryGroupingApi(groupIDs) {
    // Request group names for each system group ID.
    return DirectoryGroupingApi
      .getGroupNames(DirectoryGroupingConsts.groupTypes.systemGroup, groupIDs)
      .then((userGroups) => {
        const groupNames = map(userGroups, (group) => {
          return group.name;
        });
        return groupNames;
      })
      .catch(() => {
        throw new Error();
      });
  }

  /**
   * Override method from base class to append default filters to URL when getting items count so
   * that successfully uninstalled policies are not returned in the total count.
   */
  fetchTotalCount() {
    const filters = this.setFilterTerm(null, getFilters([], this.defaultParams.filter));
    return super.fetchTotalCount(`${this.baseUrl}?filter=${filters}`);
  }
}

export default new PolicySystemStatusApi();
